// src/hooks/useSiteLanguage.js
import { useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n from 'i18next';
import Cookies from 'js-cookie';
import { UserContext } from '../context/UserContext';
import { apiFetch } from '../utils/apiClient'; // Ensure apiFetch is imported
import { BASE_URL, IS_PRERENDERING } from '../utils/config'; // Import IS_PRERENDERING

const SITE_LANGUAGE_COOKIE = 'site_language';
const SUPPORTED_LANGUAGES = ['en', 'he', 'ru', 'es'];
const RTL_LANGUAGES = ['he'];
const DEFAULT_LANGUAGE = 'en';
export const useSiteLanguage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      if (RTL_LANGUAGES.includes(lng)) {
        document.documentElement.dir = 'rtl';
      } else {
        document.documentElement.dir = 'ltr';
      }
    };

    i18n.on('languageChanged', handleLanguageChange);
    handleLanguageChange(i18n.language); // Set initial direction
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  useEffect(() => {
    // console.log(`[useSiteLanguage] Hook triggered for path: ${location.pathname}`);
    const languageInCookie = Cookies.get(SITE_LANGUAGE_COOKIE);
    const languageInUrl = location.pathname.split('/')[1];
    // console.log(`[useSiteLanguage] Values: languageInUrl='${languageInUrl}', languageInCookie='${languageInCookie}'`);
    const setLanguage = (lang) => {
      // Always change the language in memory if it's different. This is needed for prerendering.
      if (SUPPORTED_LANGUAGES.includes(lang) && i18n.language !== lang) {
        // console.log(`[useSiteLanguage] setLanguage: Changing i18n language to '${lang}'.`);
        i18n.changeLanguage(lang);
      }
      // Only perform browser-specific side effects (cookies, API calls) if not prerendering.
      if (!IS_PRERENDERING) {
        // console.log(`[useSiteLanguage] setLanguage: Setting cookie and updating user preferences for language '${lang}'.`);
        Cookies.set(SITE_LANGUAGE_COOKIE, lang, { expires: 365 });
        // If user is logged in, save their UI language preference to the backend.
        if (user.isLoggedIn && user.token) {
          apiFetch(
            `${BASE_URL}/authUser/preferences`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ uiLanguage: lang }),
            },
            user.token
          ).catch((err) => console.error('Failed to save UI language preference:', err));
        }
      }
    };

    const detectAndSetLanguage = async () => {
      // console.log('[useSiteLanguage] detectAndSetLanguage: Attempting IP-based detection.');
      try {
        //      const response = await fetch('https://ipapi.co/json/');
        // Use our own backend endpoint, which is more reliable and not blocked by browsers.
        const response = await apiFetch(`${BASE_URL}/api/general/country-from-ip`);
        if (response.ok) {
          const data = await response.json();
          //           const countryCode = data.country_code?.toUpperCase();
          const countryCode = data.country?.toUpperCase(); // Our endpoint returns a 'country' field.
          const countryToLangMap = {
            IL: 'he',
            RU: 'ru',
            UA: 'ru',
            BY: 'ru',
            KZ: 'ru',
            ES: 'es',
            MX: 'es',
            CO: 'es',
            AR: 'es',
            PE: 'es',
            VE: 'es',
            CL: 'es',
            EC: 'es',
            GT: 'es',
            CU: 'es',
            BO: 'es',
            HN: 'es',
            PY: 'es',
            SV: 'es',
            NI: 'es',
            CR: 'es',
            PA: 'es',
            UY: 'es',
            GQ: 'es',
          };
          const detectedLang = countryToLangMap[countryCode] || DEFAULT_LANGUAGE;
          // console.log(`[useSiteLanguage] detectAndSetLanguage: Detected language '${detectedLang}' from country '${countryCode}'.`);
          const newPath = `/${detectedLang}${location.pathname}${location.search}`;
          // console.log(`[useSiteLanguage] detectAndSetLanguage: Navigating to new path: ${newPath}`);
          navigate(newPath, { replace: true });
        }
      } catch (error) {
        console.warn('[useSiteLanguage] IP-based language detection failed.', error);
      }
    };

    if (SUPPORTED_LANGUAGES.includes(languageInUrl)) {
      // console.log(`[useSiteLanguage] Condition met: Found supported language '${languageInUrl}' in URL.`);
      setLanguage(languageInUrl);
    } else if (languageInCookie && !IS_PRERENDERING) {
      // Only redirect from cookie if not prerendering
      // This prevents cookies set during one prerendered page from affecting subsequent ones
      // console.log(`[useSiteLanguage] Condition met: Redirecting based on cookie language '${languageInCookie}'.`);
      const newPath = `/${languageInCookie}${location.pathname}${location.search}`;
      navigate(newPath, { replace: true });
    } else if (!IS_PRERENDERING) {
      detectAndSetLanguage();
    }
  }, [location.pathname, location.search, navigate, user.isLoggedIn, user.token]);
  // Extract the first segment of the path.
  const potentialLangFromUrl = location.pathname.split('/')[1];
  // Return the language code only if it's in our supported list, otherwise return the default.
  const finalLang = SUPPORTED_LANGUAGES.includes(potentialLangFromUrl) ? potentialLangFromUrl : DEFAULT_LANGUAGE;

  //  console.log('useSiteLanguage: Returning language', finalLang);

  return finalLang;
};
